import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useParams, useNavigate } from 'react-router-dom'

import Counter from '../../../components/counter/Counter'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { NOT_APPLICABLE, StarRating } from '../../../components/StarRating'
import WysiwygContent from '../../../components/WysiwygContent'
import { useAppToast } from '../../../helpers'
import { IAceleraMagentaAnswer } from '../../../interfaces/IAceleraMagentaAnswer'
import AceleraMagentaService from '../../../services/AceleraMagenta/AceleraMagentaService'
import AceleraMagentaViewPage from './AceleraMagentaViewPage'

Chart.register(...registerables)

interface IFormField {
  id: number
  title: string
  type: string
  required: boolean
  options: string
  groups: string
}

interface IChartData {
  name: string
  value: number
}

interface IMetrics {
  min: number
  max: number
  message: string
}

export default function AceleraMagentaStepPage() {
  const toast = useAppToast()
  const aceleraMagentaService = new AceleraMagentaService()
  const navigate = useNavigate()
  const id = parseInt(useParams().id as string)
  const [form, setForm] = useState<IFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('')
  const [formAnswers, setFormAnswers] = useState<IAceleraMagentaAnswer>({})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [starAnswers, setStarAnswers] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [avaliacaoArpe, setAvaliacaoArpe] = useState<any[]>([])
  const [saving, setSaving] = useState<boolean>(false)
  const [chartData, setChartData] = useState<IChartData[]>([])
  const [maturityLevels, setMaturityLevels] = useState<{ maturity: string; message: string }[]>([])
  const [step, setStep] = useState<number>(0)
  const [metrics, setMetrics] = useState<IMetrics[]>([])
  const [score, setScore] = useState<number>(0)
  const modalViewAnswers = useDisclosure()
  const [firstFiveStars, setFirstFiveStars] = useState<number>(0)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)

    // valida se todos os campos foram preenchidos
    let hasEmptyField = false
    form.map((field) => {
      if (field.type === 'title') return

      if (!formAnswers[field.id] || (formAnswers[field.id] === '0' && field.type === 'stars')) {
        hasEmptyField = true
        setSaving(false)
        return
      }
    })

    if (hasEmptyField) {
      toast.invalidForm('Preencha todos os campos antes de continuar.')
      return
    }

    if (step === 5) {
      return aceleraMagentaService.update(id, {}).then((response) => {
        if (response.data.success) {
          loadStep()
        }

        setSaving(false)
      })
    }

    aceleraMagentaService.update(id, formAnswers).then((response) => {
      if (response.data.success) {
        loadStep()
      }

      setSaving(false)
    })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const loadStep = async () => {
    aceleraMagentaService.getStep(id).then((response) => {
      setFormAnswers({})
      setForm([])
      setStep(response.data.step)

      if (response.data.step === 3) {
        setFormTitle('Nível de Maturidade')
        setChartData(response.data.chart_data)
        setMaturityLevels(response.data.maturity_levels)

        return
      }

      if (response.data.step === 5) {
        setFormTitle('Delegação Eficaz')
        setMetrics(response.data.metrics)
        setScore(response.data.score)
        setForm(response.data.questions)
        const answers: IAceleraMagentaAnswer = {}
        for (const key in response.data.answers) {
          if (response.data.answers.hasOwnProperty(key)) {
            answers[parseInt(key)] = response.data.answers[key].answer
          }
        }
        setFormAnswers(answers)
      }

      if (response.data.step === 7) {
        setFormTitle('Feedback 180º Colaborador')
        setScore(Number(response.data.score.replace(/0{0,}$/, '').replace(/(\.|\,)$/, '')))
        setMetrics(response.data.metrics)
        setStarAnswers(response.data.answers)

        setFirstFiveStars(response.data.answers.find((answer: { answer: string }) => answer.answer === '5').id)
      }

      if (response.data.step === 9) {
        setFormTitle('Avaliação ARPE Colaborador')

        setAvaliacaoArpe(JSON.parse(response.data.score))

        setStarAnswers(response.data.answers)
        setFirstFiveStars(response.data.answers.find((answer: { answer: string }) => answer.answer === '5').id)
      }

      if (response.data.step === 14) {
        const nextAlignmentFieldId = response.data.questions.find((field: IFormField) => field.type === 'date')?.id

        if (nextAlignmentFieldId) {
          const today = new Date()
          const futureDate = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate())
          const formattedDate =
            futureDate.getFullYear() +
            '-' +
            (futureDate.getMonth() + 1).toString().padStart(2, '0') +
            '-' +
            futureDate.getDate().toString().padStart(2, '0')

          setFormAnswers({ [nextAlignmentFieldId]: formattedDate })
        }
      }

      if (response.data.finished) {
        navigate('/acelera-magenta/complete')

        return
      }

      setFormTitle(response.data.form.name)
      setForm(response.data.questions)
    })
  }

  useEffect(() => {
    loadStep()
  }, [])

  return (
    <>
      <PublicFormLayout title="Acelera Magenta">
        <Heading as="h2" mb="4">
          {formTitle}
          {step == 14 && (
            <>
              <Button
                colorScheme="blue"
                variant="link"
                onClick={() => modalViewAnswers.onOpen()}
                size={'sm'}
                float="right"
                marginTop="3"
              >
                Ver respostas anteriores
              </Button>
            </>
          )}
        </Heading>
        <form onSubmit={handleSubmit}>
          {step === 3 && (
            <>
              <Bar
                data={{
                  labels: ['Bebê', 'Criança', 'Adolescente', 'Adulto'],
                  datasets: [
                    {
                      label: 'Nível de Maturidade',
                      data: chartData.map((data) => data.value),
                      backgroundColor: chartData.map((data) =>
                        isBiggest(
                          data.value,
                          chartData.map((d) => d.value)
                        )
                          ? 'rgba(250, 0, 105, 0.8)'
                          : 'rgba(51, 51, 51, 0.8)'
                      )
                    }
                  ]
                }}
              />
              <br />

              {maturityLevels.map((level, key) => (
                <Box
                  key={level.maturity}
                  borderWidth="1px"
                  p="5"
                  borderRadius="lg"
                  w="100%"
                  marginBottom="3"
                  backgroundColor={
                    isBiggest(
                      chartData[key].value,
                      chartData.map((d) => d.value)
                    )
                      ? 'rgba(250, 0, 105)'
                      : 'rgba(51, 51, 51)'
                  }
                  color="#FFFFFF"
                >
                  <Heading as="h3" mb="4">
                    {level.maturity}
                  </Heading>
                  <WysiwygContent content={level.message.replaceAll('\n', '<br>')} />
                </Box>
              ))}
              <br />
            </>
          )}

          {step === 5 && (
            <>
              <Box marginBottom="5">
                <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                  <Heading as="h3" mb="4">
                    Pontuação
                  </Heading>
                  <Box
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor="magenta.500"
                    color="#FFFFFF"
                  >
                    <Text fontSize="2xl" fontWeight="bold">
                      <Center>
                        <Counter limit={score} />
                        &nbsp;pontos!
                      </Center>
                    </Text>
                  </Box>

                  {metrics.map((metric) => (
                    <Box
                      key={metric.min}
                      borderWidth="1px"
                      p="5"
                      borderRadius="lg"
                      w="100%"
                      marginBottom="3"
                      backgroundColor={
                        score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                      }
                      color="#FFFFFF"
                    >
                      <WysiwygContent
                        content={`De ${metric.min} a ${metric.max} respostas corretas - ${metric.message.replaceAll(
                          '\n',
                          '<br>'
                        )}`}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
              <Text>Resultado das respostas:</Text>
            </>
          )}

          {step === 7 && (
            <>
              <Box marginBottom="5">
                <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                  <Heading as="h3" mb="4">
                    Média das avaliações
                  </Heading>
                  <Box
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor="magenta.500"
                    color="#FFFFFF"
                  >
                    <Text fontSize="2xl" fontWeight="bold">
                      <Center>{score?.toString()?.replace('.', ',') || 0} estrelas!</Center>
                    </Text>
                  </Box>

                  {metrics.map(
                    (metric) =>
                      score >= metric.min &&
                      score <= metric.max && (
                        <Box
                          key={metric.min}
                          borderWidth="1px"
                          p="5"
                          borderRadius="lg"
                          w="100%"
                          marginBottom="3"
                          backgroundColor={
                            score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                          }
                          color="#FFFFFF"
                        >
                          <WysiwygContent content={metric.message.replaceAll('\n', '<br>')} />
                        </Box>
                      )
                  )}
                </Box>
              </Box>
              <Text>Resultado das respostas:</Text>
              {starAnswers.map((answer) => {
                return (
                  <>
                    {answer.id === firstFiveStars && (
                      <>
                        <hr />
                        <Text mb="3">
                          <strong>Parabéns pelas 5 estrelas nos itens abaixo!</strong>
                        </Text>
                      </>
                    )}
                    <FormControl isRequired={false} key={answer.id} mb="4">
                      {answer.question.title.replace(/\s/, '') && <FormLabel>{answer.question.title}</FormLabel>}
                      <StarRating value={parseInt(answer.answer)} />
                    </FormControl>
                  </>
                )
              })}
            </>
          )}

          {step === 9 && (
            <>
              Notas ARPE
              {Object.keys(avaliacaoArpe).map((key: string) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const value: number = avaliacaoArpe[key as any]
                return (
                  <>
                    <p>
                      <b>{key}</b> - {Number(value).toFixed(2)} estrelas
                    </p>
                  </>
                )
              })}
              <>
                <Box marginBottom="5" marginTop="5">
                  <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                    <Heading as="h3" mb="4">
                      <Center>Nota final ARPE</Center>
                    </Heading>
                    <Box
                      borderWidth="1px"
                      p="5"
                      borderRadius="lg"
                      w="100%"
                      marginBottom="3"
                      backgroundColor="magenta.500"
                      color="#FFFFFF"
                    >
                      <Text fontSize="2xl" fontWeight="bold">
                        <Center>
                          {(Object.values(avaliacaoArpe).reduce((a: number, b: number) => a + b, 0) / 4).toFixed(2)}{' '}
                          estrelas!
                        </Center>
                      </Text>
                    </Box>
                  </Box>
                </Box>
                <Text>Resultado das respostas:</Text>
                {starAnswers.map((answer) => {
                  return (
                    <>
                      {answer.id === firstFiveStars && (
                        <>
                          <hr />
                          <Text mb="3">
                            <strong>Parabéns pelas 5 estrelas nos itens abaixo!</strong>
                          </Text>
                        </>
                      )}
                      <FormControl isRequired={false} key={answer.id} mb="4">
                        {answer.question.title.replace(/\s/, '') && <FormLabel>{answer.question.title}</FormLabel>}
                        <StarRating value={parseInt(answer.answer)} />
                      </FormControl>
                    </>
                  )
                })}
              </>
            </>
          )}

          {form.map((field) => {
            return (
              <FormControl isRequired={step != 5 && field.type !== 'title'} key={field.id} mb="4">
                {field.title.replace(/\s/, '') && <FormLabel>{field.title}</FormLabel>}
                {field.type === 'textarea' && (
                  <Textarea
                    value={formAnswers[field.id]}
                    onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                    height="180px"
                  />
                )}
                {field.type === 'date' && (
                  <Input
                    type="date"
                    value={formAnswers[field.id]}
                    onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                  />
                )}
                {field.type === 'options' && (
                  <RadioGroup
                    value={formAnswers[field.id]}
                    onChange={(value) => (step !== 5 ? setFormAnswers({ ...formAnswers, [field.id]: value }) : '')}
                  >
                    <Stack direction="column" spacing={3}>
                      {(JSON.parse(field.options) as string[]).map((option, index) => (
                        <Radio value={(index + 1).toString()} key={index}>
                          {step !== 5 && option}

                          {step === 5 &&
                            formAnswers[field.id] == (index + 1).toString() &&
                            ((JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] == 1 && (
                              <Text color="green" fontWeight="semibold">
                                {option}
                              </Text>
                            )) ||
                              (JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] != 1 && (
                                <Text color="tomato" fontWeight="semibold">
                                  {option}
                                </Text>
                              )))}
                          {step === 5 && formAnswers[field.id] != (index + 1).toString() && option}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                )}
                {field.type === 'checkbox' && (
                  <CheckboxGroup
                    value={formAnswers[field.id] ? JSON.parse(formAnswers[field.id]) : []}
                    onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: JSON.stringify(value) })}
                  >
                    {(JSON.parse(field.options) as string[]).map((option, index) => (
                      <Checkbox value={(index + 1).toString()} key={index} marginLeft="2">
                        {option}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                )}
                {field.type === 'stars' && (
                  <Flex align="center" justify="space-between" gap="3">
                    <StarRating
                      label={field.title}
                      value={parseInt(formAnswers[field.id] ?? '0')}
                      onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: value.toString() })}
                    />
                    {!field.required && (
                      <Button
                        colorScheme="gray"
                        isActive={formAnswers[field.id] === String(NOT_APPLICABLE)}
                        type="button"
                        size={'xs'}
                        onClick={() =>
                          setFormAnswers({
                            ...formAnswers,
                            [field.id]: formAnswers[field.id] === String(NOT_APPLICABLE) ? '0' : String(NOT_APPLICABLE)
                          })
                        }
                      >
                        Não se aplica
                      </Button>
                    )}
                  </Flex>
                )}
              </FormControl>
            )
          })}

          {step < 8 ? (
            <Button colorScheme="magenta" type="submit" disabled={saving}>
              Próximo passo &nbsp;
              <Icon name="lni lni-arrow-right" />
            </Button>
          ) : (
            <Button colorScheme="magenta" type="submit" disabled={saving}>
              Assinar Feedback &nbsp;
              <Icon name="lni lni-checkmark" />
            </Button>
          )}
        </form>
      </PublicFormLayout>

      <FormModal isOpen={modalViewAnswers.isOpen} onClose={modalViewAnswers.onClose} size="compact">
        <AceleraMagentaViewPage aceleraMagentaId={id} skipLastStep={true} />
      </FormModal>
    </>
  )
}

const isBiggest = (number: number, array: number[]) => {
  return number == Math.max(...array)
}
